import { useTranslation } from "react-i18next";
import type { AnalyticsKpis } from "../../api";
import { fmtMoney } from "../../lib/format";
import { Progress } from "../ui/progress";

type Driver = { key: "services" | "products" | "returns"; delta: number };

const num = (v: string) => Number(v) || 0;

/** Δnet = Δservices + Δgross products − Δlinked goods returns. */
export function buildDrivers(kpis: AnalyticsKpis): Driver[] {
  const c = kpis.current;
  const p = kpis.previous;
  return [
    { key: "services", delta: num(c.services_revenue) - num(p.services_revenue) },
    { key: "products", delta: num(c.gross_products_revenue ?? c.products_revenue) - num(p.gross_products_revenue ?? p.products_revenue) },
    // Qaytarish O'SSA — hissa MANFIY (ayirmaning ishorasi teskari).
    { key: "returns", delta: -(num(c.linked_returns_value ?? c.returns_value) - num(p.linked_returns_value ?? p.returns_value)) },
  ].filter((d) => Math.round(d.delta) !== 0) as Driver[];
}

export function RevenueDrivers({
  kpis,
  hasProducts,
}: {
  kpis: AnalyticsKpis | null;
  hasProducts: boolean;
}) {
  const { t } = useTranslation("analytics");
  if (!kpis) return null;
  // Omborsiz tarifda (comfort) tovar va qaytarish doim nol — ro'yxat bitta
  // qatorga aylanib, "o'zgarishning 100% i xizmatlardan" degan bo'sh gap
  // bo'lardi. Bu KPI kartasining takrori, tushuntirish emas.
  if (!hasProducts) return null;

  const prev = kpis.previous;
  // Oldingi davrda umuman faoliyat bo'lmasa — taqqoslash asosi yo'q. Backend
  // ham aynan shu holatda `change_pct: null` qaytaradi, ya'ni KPI nishonlari
  // "—" ko'rsatadi; drayverlar ham jim turishi kerak.
  if (num(prev.total_revenue) === 0 && num(prev.returns_value) === 0) return null;

  const drivers = buildDrivers(kpis);
  if (drivers.length === 0) return null;

  // Ulush maxraji — |hissa|larning yig'indisi, Δnet EMAS. Xizmat +10 mln,
  // qaytarish −10 mln bo'lsa Δnet = 0 bo'lib, nolga bo'lish chiqardi;
  // |hissa| yig'indisi esa "+50% / −50%" deb to'g'ri o'qiladi.
  const denom = drivers.reduce((s, d) => s + Math.abs(d.delta), 0);
  if (denom < 1) return null;

  const rows = [...drivers].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const total = drivers.reduce((s, d) => s + d.delta, 0);

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h4 className="text-sm font-semibold">{t("drivers.title")}</h4>
        <span className="text-xs text-muted-foreground tabular-nums">
          {t("drivers.total", { value: signedMoney(total) })}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{t("drivers.subtitle")}</p>
      <ul className="space-y-2">
        {rows.map((d) => {
          const positive = d.delta > 0;
          const share = (Math.abs(d.delta) / denom) * 100;
          return (
            <li key={d.key} className="flex items-center gap-2 text-sm">
              <span className="flex-1 truncate">{t(`drivers.rows.${d.key}`)}</span>
              <span
                className={
                  "tabular-nums font-medium whitespace-nowrap " +
                  (positive ? "text-chart-positive" : "text-chart-negative")
                }
              >
                {signedMoney(d.delta)}
              </span>
              <Progress
                value={share}
                className="h-1 w-12 sm:w-16 shrink-0"
                indicatorClassName={positive ? "bg-chart-positive" : "bg-chart-negative"}
              />
              <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
                {share.toFixed(0)}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Ishorani O'ZIMIZ qo'yamiz: `groupInt` manfiy sonda ASCII "-" beradi, bu esa
 * "+" bilan bir xil kenglikda emas va `tabular-nums` ustunini buzadi.
 */
function signedMoney(v: number): string {
  const rounded = Math.round(v);
  if (rounded === 0) return fmtMoney(0);
  return (rounded > 0 ? "+" : "−") + fmtMoney(Math.abs(rounded));
}
